import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { useEffect, useRef, useState } from 'react';
import {
  DocumentProvisionJobResponse,
  ProvisionResponse,
} from 'types/api-responses';
import { errorBodyMessage } from 'utils/api-errors';

// Shown when a failed provision names no reason.
export const PROVISION_FAILED = 'Problem Provisioning Document';

const useProvisionDocument = <Payload = unknown>(provisionHref?: string) => {
  const [generatingProvision, setGeneratingProvision] = useState(false);
  const listen = useProvisionListener();

  const triggerProvision = async ({
    onError,
    onGeneratingProvision,
    onQueued,
    payload,
  }: {
    onError: (message: string) => void;
    payload?: Payload;
  } & (
    | {
        onGeneratingProvision: (provision: ProvisionResponse) => void;
        onQueued?: never;
      }
    // Takes the run over once the server has queued it, so nothing here listens.
    | { onQueued: () => void; onGeneratingProvision?: never }
  )) => {
    if (!provisionHref) return;
    setGeneratingProvision(true);

    const provisionJobResponse = await request.post<
      DocumentProvisionJobResponse,
      unknown
    >(provisionHref, payload ?? null, new AbortController());

    if (provisionJobResponse.type === 'success') {
      if (onQueued) {
        setGeneratingProvision(false);
        onQueued();
        return;
      }
      const provisionChannel = provisionJobResponse.data;
      const channel = provisionChannel.notifications.channel;
      const event = provisionChannel.notifications.events[0].name;
      listen(
        channel,
        event,
        provisionChannel.href,
        (provision) => {
          setGeneratingProvision(false);
          onGeneratingProvision?.(provision);
        },
        () => {
          setGeneratingProvision(false);
          onError(PROVISION_FAILED);
        }
      );
    } else {
      setGeneratingProvision(false);
      onError(
        errorBodyMessage(
          provisionJobResponse.type === 'error'
            ? provisionJobResponse.data
            : undefined,
          PROVISION_FAILED
        )
      );
    }
  };

  return {
    generatingProvision,
    setGeneratingProvision,
    triggerProvision,
  };
};

// Returns a disposer that unbinds this handler alone. It never unsubscribes:
// pusher-js shares one channel per name, so that would silence its other listeners.
export function subscribeToChannel(
  channel: string,
  eventName: string,
  href: string,
  onComplete: (provision: ProvisionResponse) => void,
  onError: () => void
): () => void {
  const subscription = subscribe(channel);
  let disposed = false;
  /** Re-reads the provision, and stops listening once it has finished. */
  const onProgress = async () => {
    const provisionData = await request.get<ProvisionResponse>(href);

    // Disposed while the read was in flight.
    if (disposed || 'error' in provisionData) return;

    if (provisionData.status === 'complete') {
      dispose();
      onComplete(provisionData);
    }

    if (provisionData.status === 'error') {
      dispose();
      onError();
    }
  };
  /** Removes this handler from the channel, and silences a read in flight. */
  const dispose = () => {
    disposed = true;
    subscription.unbind(eventName, onProgress);
  };

  subscription.bind(eventName, onProgress);

  return dispose;
}

/**
 * Listens for one provision at a time: listening again, or unmounting, drops
 * the last handler, so an action that runs twice never stacks them.
 * @return {Function} Takes subscribeToChannel's arguments.
 */
export function useProvisionListener() {
  const disposeRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disposeRef.current?.();
    };
  }, []);

  return (...args: Parameters<typeof subscribeToChannel>) => {
    disposeRef.current?.();
    // A request that lands after unmount must not bind a handler nobody drops.
    disposeRef.current = mountedRef.current
      ? subscribeToChannel(...args)
      : null;
  };
}

export type UseProvisionDocument = ReturnType<typeof useProvisionDocument>;
export { useProvisionDocument };
